import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { pdf } from '@react-pdf/renderer';
import * as pdfjsLib from 'pdfjs-dist/build/pdf';
import pdfWorker from 'pdfjs-dist/build/pdf.worker.min.js?url';
import { BrowserView, MobileView } from 'react-device-detect';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';

// Unified PDF preview used by EVERY invoice/receipt PDF, on both desktop and
// mobile. A sticky header toolbar exposes Save PDF + Save Image (filename = the
// invoice number passed in `fileName`). The @react-pdf document is rendered to a
// blob ONCE; desktop shows it in an <iframe> (native viewer/toolbar) and mobile
// shows pdf.js-rasterized page images (the @react-pdf <PDFViewer> iframe is blank
// on mobile). Save Image stacks all pages into one PNG.
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker;

const PdfPreview = ({ document: pdfDocument, fileName }) => {
    const [pages, setPages] = useState([]); // [{ url, w, h }]
    const [blobUrl, setBlobUrl] = useState('');
    const [building, setBuilding] = useState(true);
    const [error, setError] = useState('');
    const ranRef = useRef(false);
    const blobRef = useRef(null);

    const base = (fileName || 'document').toString().replace(/\.(pdf|png|jpe?g)$/i, '').trim() || 'document';

    useEffect(() => {
        if (ranRef.current) return undefined;
        ranRef.current = true;
        let alive = true;
        let createdUrl = '';
        (async () => {
            try {
                const blob = await pdf(pdfDocument).toBlob();
                blobRef.current = blob;
                createdUrl = URL.createObjectURL(blob);
                if (alive) setBlobUrl(createdUrl);
                const buf = await blob.arrayBuffer();
                const doc = await pdfjsLib.getDocument({ data: buf }).promise;
                const imgs = [];
                for (let i = 1; i <= doc.numPages; i += 1) {
                    // eslint-disable-next-line no-await-in-loop
                    const page = await doc.getPage(i);
                    const viewport = page.getViewport({ scale: 2 });
                    const canvas = window.document.createElement('canvas');
                    canvas.width = viewport.width;
                    canvas.height = viewport.height;
                    const ctx = canvas.getContext('2d');
                    // eslint-disable-next-line no-await-in-loop
                    await page.render({ canvasContext: ctx, viewport }).promise;
                    imgs.push({ url: canvas.toDataURL('image/png'), w: canvas.width, h: canvas.height });
                }
                if (alive) setPages(imgs);
            } catch (e) {
                if (alive) setError(e?.message || 'Cannot render PDF');
            } finally {
                if (alive) setBuilding(false);
            }
        })();
        return () => {
            alive = false;
            if (createdUrl) URL.revokeObjectURL(createdUrl);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const triggerDownload = (href, name) => {
        const a = window.document.createElement('a');
        a.href = href;
        a.download = name;
        window.document.body.appendChild(a);
        a.click();
        a.remove();
    };

    const handleSavePdf = () => {
        if (!blobRef.current) return;
        const url = URL.createObjectURL(blobRef.current);
        triggerDownload(url, `${base}.pdf`);
        setTimeout(() => URL.revokeObjectURL(url), 1500);
    };

    const handleSaveImage = async () => {
        if (!pages.length) return;
        if (pages.length === 1) {
            triggerDownload(pages[0].url, `${base}.png`);
            return;
        }
        // stack every page into one tall PNG
        const imgs = await Promise.all(
            pages.map(
                (p) =>
                    new Promise((resolve) => {
                        const im = new Image();
                        im.onload = () => resolve(im);
                        im.src = p.url;
                    })
            )
        );
        const gap = 12;
        const w = Math.max(...imgs.map((i) => i.width));
        const totalH = imgs.reduce((s, i) => s + i.height, 0) + gap * (imgs.length - 1);
        const canvas = window.document.createElement('canvas');
        canvas.width = w;
        canvas.height = totalH;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, w, totalH);
        let y = 0;
        imgs.forEach((im) => {
            ctx.drawImage(im, 0, y, im.width, im.height);
            y += im.height + gap;
        });
        triggerDownload(canvas.toDataURL('image/png'), `${base}.png`);
    };

    const Loading = (
        <Stack alignItems="center" spacing={1} sx={{ py: 5 }}>
            <CircularProgress size={28} />
            <Typography variant="caption" color="text.secondary">
                ກຳລັງສ້າງ PDF…
            </Typography>
        </Stack>
    );

    return (
        <Box sx={{ width: '100%' }}>
            {/* sticky header toolbar — visible on desktop & mobile */}
            <Stack
                direction="row"
                spacing={1}
                sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 3,
                    py: 1,
                    px: 0.5,
                    mb: 1,
                    bgcolor: 'background.paper',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    // full-width tappable buttons on mobile, right-aligned on desktop
                    justifyContent: { xs: 'stretch', sm: 'flex-end' }
                }}
            >
                <Button
                    size="small"
                    variant="contained"
                    startIcon={<PictureAsPdfIcon sx={{ fontSize: 15 }} />}
                    disabled={building}
                    onClick={handleSavePdf}
                    sx={{ flex: { xs: 1, sm: 'none' }, fontSize: '0.7rem', py: 0.35, whiteSpace: 'nowrap', minWidth: 0 }}
                >
                    Save PDF
                </Button>
                <Button
                    size="small"
                    variant="outlined"
                    color="success"
                    startIcon={<ImageIcon sx={{ fontSize: 15 }} />}
                    disabled={building || !pages.length}
                    onClick={handleSaveImage}
                    sx={{ flex: { xs: 1, sm: 'none' }, fontSize: '0.7rem', py: 0.35, whiteSpace: 'nowrap', minWidth: 0 }}
                >
                    Save Image
                </Button>
            </Stack>

            {!!error && (
                <Typography variant="body2" color="error" sx={{ py: 2, textAlign: 'center' }}>
                    ບໍ່ສາມາດສະແດງ PDF ໄດ້ ({error})
                </Typography>
            )}

            <BrowserView style={{ width: '100%' }}>
                {blobUrl ? (
                    // navpanes=0 hides only Chrome's left thumbnail sidebar; the top
                    // toolbar (zoom / page nav / print) stays.
                    <iframe
                        title="pdf-preview"
                        src={`${blobUrl}#navpanes=0`}
                        style={{ width: '100%', height: '70vh', border: 'none' }}
                    />
                ) : (
                    Loading
                )}
            </BrowserView>
            <MobileView style={{ width: '100%' }}>
                {building && Loading}
                <Stack spacing={1.2} sx={{ width: '100%', alignItems: 'center', pb: 1 }}>
                    {pages.map((img, i) => (
                        <Box
                            // eslint-disable-next-line react/no-array-index-key
                            key={i}
                            component="img"
                            src={img.url}
                            alt={`page-${i + 1}`}
                            sx={{ width: '100%', borderRadius: 1, boxShadow: 1, border: '1px solid', borderColor: 'divider' }}
                        />
                    ))}
                </Stack>
            </MobileView>
        </Box>
    );
};

PdfPreview.propTypes = {
    document: PropTypes.node,
    fileName: PropTypes.string
};

export default PdfPreview;
